import React, { useState, useEffect, useRef, useMemo } from 'react';
import { AgGridTable, Pagination, DateRangePicker, GroupedCheckboxDropdown } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { FaSearch, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa6';
import { MdFilterAlt, MdList } from 'react-icons/md';
import { getIcccc, exportIcccc } from 'redux/crm/report/reportSlice';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import { getSalesPipelines } from 'redux/crm/projectSlice';
import AdvanceFilterDrawer, { getEmptyValues } from 'components/common/AdvanceFilterDrawer';
import SalesUnitGroupedDropdown from 'components/app/crm/shared/SalesUnitGroupedDropdown';
import { columnDefs as ICCC_COLUMN_DEFS } from 'components/app/crm/reports/iccc/Column';
import { debounce } from 'helpers/debounce';

const SEARCH_FIELD = 'name_or_q_number_or_j_number_cont';

// Default to this month
const THIS_MONTH_START = dayjs().startOf('month').format('YYYY-MM-DD');
const THIS_MONTH_END   = dayjs().format('YYYY-MM-DD');

const FILTER_FIELDS = [
  { key: 'pcv_number',     label: 'PCV Number',       type: 'text', paramKey: 'id_cont',                    tagName: 'PCV' },
  { key: 'q_number',       label: 'Quote Number',      type: 'text', paramKey: 'q_number_cont',              tagName: 'Q#' },
  { key: 'j_number',       label: 'P/J Number',        type: 'text', paramKey: 'j_number_cont',              tagName: 'J#' },
  { key: 'project_name',   label: 'Project Name',      type: 'text', paramKey: 'name_cont',                  tagName: 'Project' },
  { key: 'location',       label: 'Project Location',  type: 'text', paramKey: 'pj_location_cont',           tagName: 'Location' },
  { key: 'sales_incharge', label: 'Sales Incharge',    type: 'text', paramKey: 'sales_incharge_cont',        tagName: 'Sales' },
  { key: 'customer_name',  label: 'Customer Name',     type: 'text', paramKey: 'company_name_cont',          tagName: 'Customer' },
  { key: 'enduser_name',   label: 'Enduser Name',      type: 'text', paramKey: 'enduser_customer_name_cont', tagName: 'Enduser' },
  { key: 'created_by',     label: 'Created By',        type: 'text', paramKey: 'user_name_cont',             tagName: 'CreatedBy' },
];


const ICCCPage = () => {
  const dispatch = useDispatch();
  const { icccData }               = useSelector((s) => s.crm_report);
  const { activeSalesUnitsData = [] } = useSelector((s) => s.crm_sales_unit);
  const { salesPipelines = [] }    = useSelector((s) => s.crm_project);
  const { data = [], totalRows = 0, totalPagesCount = 1, exporting = false } = icccData || {};

  const [page, setPage]             = useState(1);
  const [perPage, setPerPage]       = useState(50);
  const [sort, setSort]             = useState({ attribute: 'id', direction: 'desc' });
  const [search, setSearch]         = useState('');
  const [filterValues, setFilterValues] = useState(() => getEmptyValues(FILTER_FIELDS));
  const [filterTags, setFilterTags] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [loading, setLoading]       = useState(false);

  // Quick filters — Created Date defaults to this month
  const [quickCreatedDate, setQuickCreatedDate] = useState({ startDate: THIS_MONTH_START, endDate: THIS_MONTH_END });
  const [quickSalesUnitIds, setQuickSalesUnitIds] = useState([]);
  const [quickPipelineIds, setQuickPipelineIds]   = useState([]);

  // Refs for always-fresh values inside async handlers
  const searchRef             = useRef('');
  const activeFilterParamsRef = useRef({});
  const sortRef               = useRef(sort);
  const perPageRef            = useRef(perPage);
  const pageRef               = useRef(page);
  const quickCreatedRef       = useRef({ startDate: THIS_MONTH_START, endDate: THIS_MONTH_END });
  const quickUnitsRef         = useRef([]);
  const quickPipelineRef      = useRef([]);

  sortRef.current          = sort;
  perPageRef.current       = perPage;
  pageRef.current          = page;
  quickCreatedRef.current  = quickCreatedDate;
  quickUnitsRef.current    = quickSalesUnitIds;
  quickPipelineRef.current = quickPipelineIds;

  const totalPages = totalPagesCount || Math.ceil((totalRows || 0) / perPage) || 1;

  // Build GroupedCheckboxDropdown groups from salesPipelines (grouped by group_name)
  const pipelineGroups = useMemo(() => {
    const groupMap = {};
    salesPipelines.forEach((sp) => {
      const groupKey = sp.group_name || 'Other';
      if (!groupMap[groupKey]) {
        groupMap[groupKey] = { key: groupKey, label: groupKey, items: [] };
      }
      groupMap[groupKey].items.push({
        value: sp.id,
        label: sp.name,
        color: sp.color,
      });
    });
    return Object.values(groupMap);
  }, [salesPipelines]);

  useEffect(() => {
    dispatch(getActiveSalesUnits());
    dispatch(getSalesPipelines());
  }, [dispatch]);

  // ── Fetch helpers ────────────────────────────────────────────────
  const doFetch = async (params) => {
    setLoading(true);
    try { await dispatch(getIcccc(params)); } finally { setLoading(false); }
  };

  const buildBase = (overrides = {}) => ({
    sort:     sortRef.current,
    per_page: perPageRef.current,
    page:     pageRef.current,
    ...(searchRef.current ? { [SEARCH_FIELD]: searchRef.current } : {}),
    ...activeFilterParamsRef.current,
    ...overrides,
  });

  const buildDateParam = (range) => {
    const p = {};
    // Use startOf/endOf day with local timezone offset so Rails doesn't interpret as UTC midnight
    if (range.startDate) p.created_at_gteq = dayjs(range.startDate).startOf('day').format();
    if (range.endDate)   p.created_at_lteq = dayjs(range.endDate).endOf('day').format();
    return p;
  };

  const buildUnitsParam = (ids) =>
    ids.length ? { sales_unit_id_in: ids } : {};

  const buildPipelineParam = (ids) =>
    ids.length ? { sales_pipeline_id_in: ids } : {};

  const buildAllParams = (overrides = {}) => {
    const base = buildBase(overrides);
    return {
      ...base,
      ...buildDateParam(quickCreatedRef.current),
      ...buildUnitsParam(quickUnitsRef.current),
      ...buildPipelineParam(quickPipelineRef.current),
      ...overrides,
    };
  };

  // ── Fetch on page / perPage / sort change ────────────────────────
  useEffect(() => {
    doFetch(buildAllParams({ page, per_page: perPage, sort }));
    // doFetch/buildAllParams are re-created every render (they read refs), so listing them
    // would refetch on every render; the fetch is driven by page/perPage/sort only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, sort]);

  const debouncedSearch = useRef(debounce((params) => doFetch(params), 500)).current;

  // ── Handlers ────────────────────────────────────────────────────
  const handleSearchChange = (e) => {
    const val = e.target.value;
    searchRef.current = val;
    setSearch(val);
    const searchOverride = val ? { [SEARCH_FIELD]: val } : {};
    debouncedSearch(buildAllParams({ page: 1, ...searchOverride }));
  };

  const handleClearSearch = () => {
    searchRef.current = '';
    setSearch('');
    setPage(1);
    doFetch(buildAllParams({ page: 1 }));
  };

  const handleSort = ({ colId, sort: dir }) => {
    const newSort = dir ? { attribute: colId, direction: dir } : { attribute: 'id', direction: 'desc' };
    sortRef.current = newSort;
    setSort(newSort);
    setPage(1);
    doFetch(buildAllParams({ page: 1, sort: newSort }));
  };

  const handleCreatedDateChange = (range) => {
    quickCreatedRef.current = range;
    setQuickCreatedDate(range);
    setPage(1);
    doFetch({
      ...buildBase({ page: 1 }),
      ...buildDateParam(range),
      ...buildUnitsParam(quickUnitsRef.current),
      ...buildPipelineParam(quickPipelineRef.current),
    });
  };

  const handleSalesUnitChange = (ids) => {
    quickUnitsRef.current = ids;
    setQuickSalesUnitIds(ids);
    setPage(1);
    doFetch({
      ...buildBase({ page: 1 }),
      ...buildDateParam(quickCreatedRef.current),
      ...buildUnitsParam(ids),
      ...buildPipelineParam(quickPipelineRef.current),
    });
  };

  const handlePipelineChange = (ids) => {
    quickPipelineRef.current = ids;
    setQuickPipelineIds(ids);
    setPage(1);
    doFetch({
      ...buildBase({ page: 1 }),
      ...buildDateParam(quickCreatedRef.current),
      ...buildUnitsParam(quickUnitsRef.current),
      ...buildPipelineParam(ids),
    });
  };

  const handleApplyFilter = (filterParams, tags) => {
    activeFilterParamsRef.current = filterParams;
    setFilterTags(tags);
    setPage(1);
    doFetch({
      ...buildBase({ page: 1 }),
      ...buildDateParam(quickCreatedRef.current),
      ...buildUnitsParam(quickUnitsRef.current),
      ...buildPipelineParam(quickPipelineRef.current),
      ...filterParams,
    });
  };

  const handleResetFilter = () => {
    activeFilterParamsRef.current = {};
    setFilterValues(getEmptyValues(FILTER_FIELDS));
    setFilterTags([]);
    setPage(1);
    doFetch({
      ...buildBase({ page: 1 }),
      ...buildDateParam(quickCreatedRef.current),
      ...buildUnitsParam(quickUnitsRef.current),
      ...buildPipelineParam(quickPipelineRef.current),
    });
  };

  const removeTag = (tag) => {
    const newParams = Object.fromEntries(
      Object.entries(activeFilterParamsRef.current).filter(([k]) => k !== tag.paramKey),
    );
    activeFilterParamsRef.current = newParams;
    setFilterTags((prev) => prev.filter((t) => t.id !== tag.id));
    setPage(1);
    doFetch({
      ...buildBase({ page: 1 }),
      ...buildDateParam(quickCreatedRef.current),
      ...buildUnitsParam(quickUnitsRef.current),
      ...buildPipelineParam(quickPipelineRef.current),
      ...newParams,
    });
  };

  const handleReload = () => doFetch(buildAllParams());

  const handleExport = () =>
    dispatch(exportIcccc(buildAllParams({ page: 1, per_page: totalRows || 5000 })));

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* Header */}
      <div className="tw-mb-1">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0"
              style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
            <div className="tw-flex tw-items-center tw-gap-2">
              <MdList className="tw-text-primary-600" size={22} />
              <div>
                <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">ICCC Report</h1>
                <p className="tw-text-gray-500 tw-text-sm tw-mb-0">
                  Project pipeline and commercial data for ICCC reporting.
                </p>
              </div>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <button type="button" onClick={handleReload} disabled={loading}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer disabled:tw-opacity-50">
              <FaSyncAlt size={12} className={loading ? 'tw-animate-spin' : ''} /> Reload
            </button>
            <button type="button" onClick={handleExport} disabled={exporting || loading}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-green-300 tw-bg-green-50 tw-text-green-700 hover:tw-bg-green-100 tw-text-sm tw-font-medium tw-cursor-pointer disabled:tw-opacity-50">
              <FaFileExcel size={13} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {/* Filter bar — no overflow:hidden so portalled dropdowns aren't clipped */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">

          {/* Search */}
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-52 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Project name, Q#, J#..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
            />
            {search && (
              <button type="button" onClick={handleClearSearch}
                className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Created Date quick filter */}
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-whitespace-nowrap">Created</span>
            <DateRangePicker
              label="Select date range"
              startDate={quickCreatedDate.startDate}
              endDate={quickCreatedDate.endDate}
              onChange={handleCreatedDateChange}
            />
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Sales Unit checkbox dropdown */}
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-whitespace-nowrap">Sales Unit</span>
            <SalesUnitGroupedDropdown
              units={activeSalesUnitsData}
              selectedIds={quickSalesUnitIds}
              onChange={handleSalesUnitChange}
              placeholder="All units"
            />
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* Sales Pipeline filter */}
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-whitespace-nowrap">Pipeline</span>
            <GroupedCheckboxDropdown
              groups={pipelineGroups}
              selectedIds={quickPipelineIds}
              onChange={handlePipelineChange}
              placeholder="All"
              width={280}
            />
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          {/* More Filters */}
          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
              filterTags.length > 0
                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700'
                : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'
            }`}
          >
            <MdFilterAlt size={15} />
            More Filters
            {filterTags.length > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">
                {filterTags.length}
              </span>
            )}
          </button>

          {!loading && totalRows > 0 && (
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
              {totalRows.toLocaleString()} records
            </span>
          )}
        </div>

        {/* Active More-Filter tags */}
        {filterTags.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-primary-50/40">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-flex-shrink-0">Active filters:</span>
            {filterTags.map((tag) => (
              <span key={tag.id}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-1 tw-py-0.5 tw-bg-white tw-border tw-border-primary-200 tw-text-slate-700 tw-rounded-md tw-text-xs tw-shadow-sm">
                <span className="tw-text-primary-500 tw-font-semibold">{tag.name}</span>
                <span className="tw-text-slate-300 tw-mx-0.5">·</span>
                <span className="tw-max-w-[140px] tw-truncate tw-text-slate-600">
                  {Array.isArray(tag.value) ? tag.value.join(' – ') : tag.value}
                </span>
                <button type="button" onClick={() => removeTag(tag)}
                  className="tw-ml-0.5 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors">
                  <FaTimes size={8} />
                </button>
              </span>
            ))}
            <button type="button" onClick={handleResetFilter}
              className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-md tw-text-xs tw-text-red-500 hover:tw-bg-red-500 hover:tw-text-white tw-border tw-border-red-200 hover:tw-border-red-500 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-font-medium">
              <FaTimes size={8} /> Clear all
            </button>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <AgGridTable
          rowData={data}
          columnDefs={ICCC_COLUMN_DEFS}
          loading={loading}
          rowNumbers
          pagination={false}
          filter={false}
          height="max(320px, calc(100dvh - 290px))"
          onSortChanged={handleSort}
        />
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalRows}
        perPage={perPage}
        pageSizeOptions={[20, 50, 100, 200]}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
        disabled={loading}
      />

      <AdvanceFilterDrawer
        show={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={handleApplyFilter}
        onReset={handleResetFilter}
        fields={FILTER_FIELDS}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        title="Advanced Filters — ICCC Report"
      />
    </div>
  );
};

export default ICCCPage;
